// src/pages/tilbud/TilbudVisning.jsx
// Det kunden ser av pristilbudet: toppen (ref., dato, gyldighet) og «Tilbudsoversikt» med varer,
// rabatt, total og kommentar. Felles for kundesiden (PublicOfferPortal) og forhåndsvisningen i
// ansatt-appen (EditOffer), så forhåndsvisningen alltid viser det samme som kunden får.
import React from "react";

export const fmtNOK = (n) =>
  new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

// Rabatten på en linje slik tilbudet skal vise den (offer.rabattVisning): «-10%» eller «−600 kr».
export function rabattTekst(p, visning) {
  if (visning === "kr") {
    const kr = Number(p.totalRabattKr ?? p.rabattKr) || 0;
    return kr > 0 ? `−${fmtNOK(kr)}` : "";
  }
  return Number(p.rabatt) > 0 ? `-${p.rabatt}%` : "";
}

export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(+dt) ? "—" : dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// overskrift: nivået på «Tilbud» (h1 på kundesiden, lavere inne i en dialog).
export function TilbudTopp({ offer, merke, overskrift = "h1" }) {
  const Tittel = overskrift;
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <Tittel className="text-xl sm:text-2xl font-bold text-gray-900">Tilbud</Tittel>
        {offer.klubbNavn && (
          <p className="mt-0.5 font-semibold text-gray-800 [overflow-wrap:anywhere]">Til {offer.klubbNavn}</p>
        )}
        {offer.offerRef && (
          <p className="text-xs text-gray-500 mt-0.5">Ref. #{offer.offerRef}</p>
        )}
        <div className="flex flex-wrap gap-x-3 mt-1 text-xs text-gray-600">
          <span>{fmtDate(offer.registrertDato)}</span>
          {offer.utloper && <span>Gyldig til {fmtDate(offer.utloper)}</span>}
        </div>
      </div>
      {merke}
    </div>
  );
}

export function TilbudOversikt({ offer, totalPris, overskrift = "h2" }) {
  const Under = overskrift;
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-gray-50 border-b px-4 py-3">
        <Under className="font-semibold text-gray-900 text-sm">Tilbudsoversikt</Under>
      </div>

      {(offer.produkter || []).length > 0 ? (<>
        {/* Mobil: kortvisning */}
        <div className="sm:hidden divide-y divide-gray-100">
          {(offer.produkter || []).map((p, i) => (
            <div key={i} className="px-4 py-3">
              <div className="flex justify-between items-start gap-2">
                <span className="font-medium text-gray-900 text-sm leading-snug">{p.artikkel || "—"}</span>
                <span className="font-semibold text-gray-900 text-sm shrink-0">{fmtNOK(p.totalprisEtterRabatt)}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {(p.antall || 1) > 1 && <span>{p.antall} stk</span>}
                <span className="text-gray-600">Veil. {fmtNOK(p.pris)}</span>
                {rabattTekst(p, offer.rabattVisning) && (
                  <span className="bg-green-50 text-green-700 rounded-full px-2 py-0.5 font-medium">{rabattTekst(p, offer.rabattVisning)}</span>
                )}
              </div>
            </div>
          ))}
          <div className="px-4 py-3 bg-gray-50 border-t-2 border-gray-200 flex justify-between items-center">
            <span className="font-bold text-gray-900 text-sm">Totalt</span>
            <span className="font-bold text-gray-900">{fmtNOK(totalPris)}</span>
          </div>
        </div>

        {/* Desktop: tabellvisning */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th scope="col" className="px-4 py-2 text-left font-medium text-gray-600">Artikkel</th>
                <th scope="col" className="px-3 py-2 text-center font-medium text-gray-600">Ant.</th>
                <th scope="col" className="px-3 py-2 text-right font-medium text-gray-600">Stykk</th>
                <th scope="col" className="px-3 py-2 text-center font-medium text-gray-600">Rabatt</th>
                <th scope="col" className="px-4 py-2 text-right font-medium text-gray-600">Pris</th>
              </tr>
            </thead>
            <tbody>
              {(offer.produkter || []).map((p, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-gray-900">{p.artikkel || "—"}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{p.antall || 1}</td>
                  <td className="px-3 py-3 text-right text-gray-500">{fmtNOK(p.pris)}</td>
                  <td className="px-3 py-3 text-center">
                    {rabattTekst(p, offer.rabattVisning) ? (
                      <span className="bg-green-50 text-green-700 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">{rabattTekst(p, offer.rabattVisning)}</span>
                    ) : <span className="text-gray-600 text-xs"><span aria-hidden="true">—</span><span className="sr-only">Ingen rabatt</span></span>}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmtNOK(p.totalprisEtterRabatt)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-200">
                <td colSpan={4} className="px-4 py-3 font-bold text-gray-900">Totalt</td>
                <td className="px-4 py-3 text-right font-bold text-gray-900 text-base">{fmtNOK(totalPris)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </>) : (
        <p className="px-4 py-6 text-center text-gray-600 text-sm">Ingen produkter i dette tilbudet.</p>
      )}

      {offer.kommentar?.trim() && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
          <p className="text-xs font-medium text-gray-500 mb-1">Kommentar fra butikken</p>
          <p className="text-sm text-gray-700" style={{ whiteSpace: "pre-wrap" }}>{offer.kommentar.trim()}</p>
        </div>
      )}
    </section>
  );
}
